import type { CoverageDto } from '@mnemo/shared';
import type { TranslationKey } from '../i18n.js';

type Props = {
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  coverage: CoverageDto;
  selected: string | null;
  onSelect: (period: string | null) => void;
  numberFormat: Intl.NumberFormat;
};

/**
 * La frise de couverture — le premier écran de Mnemo, et son argument.
 *
 * Elle montre d'un coup d'œil ce que l'application détient VRAIMENT, mois par mois, face à
 * la fenêtre visée. C'est la réponse à la question que se posent les écoles — *« jusqu'où
 * puis-je remonter ? »* — et c'est aussi ce qui rend visible, sans rapport ni réunion, un
 * mois que la collecte aurait manqué.
 */
export function Coverage({ t, coverage, selected, onSelect, numberFormat }: Props) {
  if (coverage.months.length === 0) {
    return (
      <section className="card p-4">
        <h2 className="text-sm font-semibold text-muted">{t('coverage.title')}</h2>
        <p className="mt-2 text-muted">{t('coverage.empty')}</p>
      </section>
    );
  }

  const max = Math.max(...coverage.months.map((m) => m.jobs));

  return (
    <section className="card px-3 pb-2 pt-2.5">
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">
          {t('coverage.title')}
        </h2>
        <p className="text-xs text-muted">
          {t('coverage.months', {
            count: coverage.months.length,
            target: coverage.retentionMonths,
          })}{' '}
          · {t('coverage.total', { count: numberFormat.format(coverage.totalJobs) })}
        </p>
      </div>

      {/* Défile horizontalement sur mobile : dix-huit mois ne tiennent pas sur un téléphone. */}
      <ul className="-mx-4 flex items-end gap-1 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
        {coverage.months.map((m) => {
          const active = selected === m.period;
          return (
            <li key={m.period} className="min-w-[3rem] max-w-[6rem] flex-1">
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onSelect(active ? null : m.period)}
                title={`${m.period} · ${numberFormat.format(m.jobs)}`}
                className={`group flex min-h-[44px] w-full flex-col items-center justify-end gap-0.5 rounded-lg px-1 py-1 transition-colors ${
                  active ? 'bg-accent/15' : 'hover:bg-bg'
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`w-full rounded-t-sm ${active ? 'bg-accent' : 'bg-blue/50 group-hover:bg-blue/70'}`}
                  style={{ height: `${Math.max(3, Math.round((m.jobs / max) * 26))}px` }}
                />
                <span
                  className={`text-[10px] tabular-nums ${active ? 'font-semibold text-fg' : 'text-muted'}`}
                >
                  {m.period.slice(2)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
